import { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, DuoColorIcon, Icon, Text } from '@zillow/constellation-10';
import { IconChecklistFilled, IconFileFilled } from '@zillow/constellation-icons';
import ActiveCard from './ActiveCard';
import EmptyCard from './EmptyCard';
import ErrorCard from './ErrorCard';
import { LINKS } from '../../constants';
import type {
  YourRentalErrorDto,
  YourRentalPropertyConditionDto,
  YourRentalPropertyConditionSectionDto,
  YourRentalStatusTag,
} from '@zg-rentals/ts-your-rental-api-client';

const CONTENT = {
  EMPTY_TITLE: 'Add a property condition report',
  BODY: 'Renters who document move-in conditions are more likely to recover their full deposit. Send your landlord the link below and ask them to get started.',
  CTA: 'Copy link to invite landlord',
} as const;

type PropertyConditionCardProps = {
  propertyConditionSection?: YourRentalPropertyConditionSectionDto;
  listingAlias?: string;
};

type PropertyConditionActiveCardProps = {
  defaultReportIndex: number;
  reports: Array<YourRentalPropertyConditionDto>;
  listingAlias?: string;
};

const PropertyConditionEmptyCard = () => {
  return (
    <EmptyCard
      icon={
        <DuoColorIcon tone="trust">
          <Icon>
            <IconChecklistFilled />
          </Icon>
        </DuoColorIcon>
      }
      title={CONTENT.EMPTY_TITLE}
      description={CONTENT.BODY}
      link={LINKS.RENTAL_MANAGER_LEASE_AGREEMENTS}
      buttonText={CONTENT.CTA}
      dataTestId="PropertyCondition"
      shouldCopyLink={true}
    />
  );
};

const PropertyConditionErrorCard = ({ error }: { error: YourRentalErrorDto }) => {
  return (
    <ErrorCard
      icon={
        <DuoColorIcon tone="info">
          <Icon>
            <IconFileFilled />
          </Icon>
        </DuoColorIcon>
      }
      title={error.cardTitle}
      error={error}
      dataTestId="PropertyCondition"
    />
  );
};

const PropertyConditionActiveCard = ({
  defaultReportIndex,
  reports,
  listingAlias,
}: PropertyConditionActiveCardProps) => {
  const [selectedIndex, setSelectedIndex] = useState(defaultReportIndex);

  const selectedReport = useMemo(() => reports[selectedIndex], [reports, selectedIndex]);

  const reportOptions = useMemo(() => reports.map((_, index) => `Report #${index + 1}`), [reports]);

  useEffect(() => {
    setSelectedIndex(defaultReportIndex);
  }, [reports, defaultReportIndex]);

  const handleReportChange = useCallback((option: string) => {
    const index = parseInt(option.replace('Report #', '')) - 1;
    setSelectedIndex(index);
  }, []);

  const tags = useMemo(() => {
    const tagList: Array<YourRentalStatusTag> = [selectedReport.statusTag];
    if (selectedReport.phaseTag) {
      tagList.push(selectedReport.phaseTag);
    }
    return tagList;
  }, [selectedReport.statusTag, selectedReport.phaseTag]);

  return (
    <ActiveCard
      title={selectedReport.cardTitle}
      dataTestId="PropertyConditionActiveCard"
      icon={
        <DuoColorIcon tone="info">
          <Icon>
            <IconFileFilled />
          </Icon>
        </DuoColorIcon>
      }
      dropdownOptions={reports.length > 1 ? reportOptions : undefined}
      dropdownValue={`Report #${selectedIndex + 1}`}
      onDropdownChange={handleReportChange}
      dropdownAriaLabel="Select property condition report"
      tags={tags}
      ctas={selectedReport.ctas}
      listingAlias={listingAlias}
    >
      {selectedReport.completeTenantText && (
        <Box
          css={{
            display: 'flex',
            flexDirection: 'column',
            gap: 'micro.default',
          }}
        >
          <Text textStyle="body-lg-bold">{selectedReport.completeTenantText}</Text>
        </Box>
      )}
    </ActiveCard>
  );
};

const PropertyConditionCard = ({ propertyConditionSection, listingAlias }: PropertyConditionCardProps) => {
  const showUpsell =
    !propertyConditionSection ||
    propertyConditionSection.upsellCardType ||
    propertyConditionSection.reports.length === 0;

  if (propertyConditionSection?.error) {
    return <PropertyConditionErrorCard error={propertyConditionSection.error} />;
  }

  return showUpsell ? (
    <PropertyConditionEmptyCard />
  ) : (
    <PropertyConditionActiveCard
      defaultReportIndex={propertyConditionSection.defaultReportIndex || 0}
      reports={propertyConditionSection.reports}
      listingAlias={listingAlias}
    />
  );
};

export default PropertyConditionCard;
